import { Box, IconButton, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { History as HistoryIcon } from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";
import { AuiProvider, Suggestions, useAui } from "@assistant-ui/react";
import { Thread } from "../components/assistant-ui/thread";
import { SimpleChatRuntimeProvider } from "../chat-v2/SimpleChatRuntime";
import { ChatSidebar } from "../chat-v2/ChatSidebar";
import { DraftSync } from "../chat-v2/DraftSync";
import ChatErrorBoundary from "../chat-v2/ChatErrorBoundary";
import { useAppSelector } from "../store/hooks";

// Static welcome-screen suggestion cards. Shown ONLY while the thread
// is empty (no messages yet); replaced by `follow_up_questions` from
// the last assistant message after the first turn — see the suggestions
// memo in ChatSurface below.
const WELCOME_SUGGESTIONS = [
  {
    title: "What are AGRANA's latest products?",
    label: "Discover new innovations",
    prompt: "What are AGRANA's latest products?",
  },
  {
    title: "Tell me about AGRANA's sustainability initiatives",
    label: "Learn about environmental efforts",
    prompt: "Tell me about AGRANA's sustainability initiatives",
  },
  {
    title: "How can I optimize fruit processing with AGRANA solutions?",
    label: "Get expert recommendations",
    prompt: "How can I optimize fruit processing with AGRANA solutions?",
  },
];

function ChatSurface() {
  // Constant welcome cards (empty-thread only). Dynamic follow-ups live on
  // the runtime's thread.suggestions instead — see thread.tsx.
  const suggestions = useMemo(() => Suggestions(WELCOME_SUGGESTIONS), []);
  const aui = useAui({ suggestions });
  return (
    <AuiProvider value={aui}>
      <DraftSync />
      <Thread />
    </AuiProvider>
  );
}

// Full chat page — sidebar (sessions) + main pane (thread + composer).
// Designed to live inside DashboardLayout's main content area (navbar +
// app sidebar already provided by the layout), so this page only owns
// the chat-internal layout. Replaces legacy pages/Chatbot.tsx; see
// [[project-assistant-ui-migration]] and [[feedback-migration-feature-parity]].
export default function ChatV2() {
  const theme = useTheme();
  // Keys the runtime + error boundary so each session gets a fresh thread,
  // avoiding assistant-ui's by-index crash when swapping conversations.
  const activeSessionId = useAppSelector(
    (s) => s.chat.currentSession?.id ?? "new",
  );
  // Lifted above the keyed runtime so streaming survives the remount.
  const [isRunning, setIsRunning] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const scopeRef = useRef<HTMLDivElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  // Auto-close the chat sidebar when shrinking to mobile, auto-open
  // when growing back to desktop. Without this the drawer state gets
  // stuck across a viewport resize.
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  // Mirror MUI's palette.mode onto the shadcn .dark class so the
  // assistant-ui surface follows whatever theme toggle the user has set
  // in the app.
  useEffect(() => {
    const el = scopeRef.current;
    if (!el) return;
    if (theme.palette.mode === "dark") el.classList.add("dark");
    else el.classList.remove("dark");
  }, [theme.palette.mode]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 64px)", // below DashboardLayout's navbar
        overflow: "hidden",
      }}
    >
      <ChatSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        {/* Mobile header — sidebar toggle + title. Desktop hides this; the
            app navbar already provides the page title via DashboardLayout. */}
        {isMobile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 2,
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
              backgroundColor: "background.paper",
              position: "relative",
            }}
          >
            <IconButton
              onClick={() => setSidebarOpen(true)}
              sx={{
                color: "text.primary",
                position: "absolute",
                left: 16,
              }}
              aria-label="Open chat history"
            >
              <HistoryIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                color: "text.primary",
                textAlign: "center",
                width: "100%",
              }}
            >
              Chatbot
            </Typography>
          </Box>
        )}

        {/* Thread surface. The aui-scope wrapper bridges MUI's palette.mode
            into the shadcn `.dark` class so the assistant-ui visuals follow
            the app theme. */}
        <Box
          ref={scopeRef}
          className="aui-scope"
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f0f0f" : "#fafafa",
          }}
        >
          <ChatErrorBoundary resetKey={activeSessionId}>
            <SimpleChatRuntimeProvider
              key={activeSessionId}
              isRunning={isRunning}
              setIsRunning={setIsRunning}
            >
              <ChatSurface />
            </SimpleChatRuntimeProvider>
          </ChatErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
}
